/**
 * Generating subtitles from the audio (speech to text). The worker extracts the
 * audio from the proxy, asks the speech service (Whisper) for text with times,
 * and returns ready cues. Everything else (the track, the audit) is recorded by the
 * control API from the result.
 */
import { z } from 'zod';
import { CONTRACT_VERSION, LocationSpecsSchema, ObjectRefSchema } from './jobs.ts';
import { CueSchema } from './subtitles.ts';

/** What people can choose: detect the language, or one of the languages we offer. */
export const TRANSCRIBE_LANGUAGES = ['auto', 'fi', 'sv', 'en'] as const;
export type TranscribeLanguage = (typeof TRANSCRIBE_LANGUAGES)[number];

export const TranscribeJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  transcriptionId: z.string().uuid(),
  /** The proxy (its first audio track; proxies already mix two mono tracks as left and right). */
  source: ObjectRefSchema,
  locations: LocationSpecsSchema,
  language: z.enum(TRANSCRIBE_LANGUAGES),
  /** The media's length, so no cue runs past its end. */
  durationTicks: z.number().int().nonnegative().nullable(),
});
export type TranscribeJob = z.infer<typeof TranscribeJobSchema>;

export const TranscribeResultSchema = z.object({
  cues: z.array(CueSchema).max(20_000),
  /** The language Whisper used, as an ISO 639-1 code (null if it named one we do not know). */
  language: z.string().nullable(),
  /** Seconds of audio sent to the speech service. */
  audioSeconds: z.number().nonnegative(),
  /** Seconds the speech service took. */
  speechSeconds: z.number().nonnegative(),
});
export type TranscribeResult = z.infer<typeof TranscribeResultSchema>;

/** Names for the label of a generated track ("Finnish (automatic)"); other detected languages use Intl names. */
export function transcriptLabel(language: string | null): string {
  let name = 'Unknown language';
  if (language) {
    try {
      name = new Intl.DisplayNames(['en'], { type: 'language' }).of(language) ?? language;
    } catch {
      name = language;
    }
  }
  return `${name} (automatic)`;
}
